/**
 * Sync queue management for tracking and retrying failed syncs
 * Implements exponential backoff for retries
 */

import { addToSyncQueue, clearSyncQueue, getSyncQueue, removeFromSyncQueue } from "../offline/db";
import type { SyncQueueItem } from "../offline/types";

const MAX_RETRY_COUNT = 5;
const BASE_RETRY_DELAY_MS = 1000; // 1 second

/**
 * Get all items in sync queue
 */
export async function getQueueItems(): Promise<SyncQueueItem[]> {
  return await getSyncQueue();
}

/**
 * Get items that are ready to retry (not too many retries, enough time passed)
 */
export async function getRetryableItems(): Promise<SyncQueueItem[]> {
  const items = await getSyncQueue();

  return items.filter((item) => {
    // Skip if too many retries
    if (item.retry_count >= MAX_RETRY_COUNT) {
      return false;
    }

    // If never attempted, it's retryable
    if (!item.last_attempted_at) {
      return true;
    }

    // Check if enough time has passed for exponential backoff
    const lastAttempt = new Date(item.last_attempted_at).getTime();
    const now = Date.now();
    const delayMs = calculateBackoffDelay(item.retry_count);

    return now - lastAttempt >= delayMs;
  });
}

/**
 * Calculate exponential backoff delay
 */
export function calculateBackoffDelay(retryCount: number): number {
  // Exponential backoff: 1s, 2s, 4s, 8s, 16s
  return BASE_RETRY_DELAY_MS * 2 ** retryCount;
}

/**
 * Add item to sync queue
 */
export async function enqueue(item: SyncQueueItem): Promise<void> {
  await addToSyncQueue(item);
}

/**
 * Remove item from sync queue (after successful sync)
 */
export async function dequeue(id: string): Promise<void> {
  await removeFromSyncQueue(id);
}

/**
 * Update retry count and last attempted timestamp
 */
export async function markRetryAttempt(item: SyncQueueItem): Promise<void> {
  item.retry_count += 1;
  item.last_attempted_at = new Date().toISOString();
  await addToSyncQueue(item); // Updates existing item
}

/**
 * Mark item as failed with error
 */
export async function markFailed(item: SyncQueueItem, error: string): Promise<void> {
  item.error = error;
  item.last_attempted_at = new Date().toISOString();
  await addToSyncQueue(item);
}

/**
 * Get items that have exceeded max retries
 */
export async function getFailedItems(): Promise<SyncQueueItem[]> {
  const items = await getSyncQueue();
  return items.filter((item) => item.retry_count >= MAX_RETRY_COUNT);
}

/**
 * Clear all items from queue
 */
export async function clearQueue(): Promise<void> {
  await clearSyncQueue();
}

/**
 * Get queue statistics
 */
export async function getQueueStats(): Promise<{
  total: number;
  retryable: number;
  failed: number;
  pending: number;
}> {
  const items = await getSyncQueue();
  const retryable = await getRetryableItems();
  const failed = await getFailedItems();

  return {
    total: items.length,
    retryable: retryable.length,
    failed: failed.length,
    pending: items.filter((item) => item.retry_count === 0).length,
  };
}
